import axios from 'axios';

const TEMPLATE_STUDIO_TENANT_ID = 'e313a733-e602-4bfb-a5e9-67b2a5e74ba8';
const API_URL = process.env.REACT_APP_API_URL || '';

const studioApi = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

studioApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('sa_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Tenant-ID'] = TEMPLATE_STUDIO_TENANT_ID;
  return config;
});

studioApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sa_token');
      localStorage.removeItem('sa_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Mirror the adminApi interface from lf-frontend, scoped to Template Studio
export const studioAdminApi = {
  getRecordTypes: () => studioApi.get('/admin/record-types'),
  getRecordType: (id) => studioApi.get(`/admin/record-types/${id}`),
  updateRecordType: (id, data) => studioApi.put(`/admin/record-types/${id}`, data),

  getFields: (recordTypeId) => studioApi.get(`/admin/fields?record_type_id=${recordTypeId}`),
  createField: (data) => studioApi.post('/admin/fields', data),
  updateField: (id, data) => studioApi.put(`/admin/fields/${id}`, data),
  deleteField: (id) => studioApi.delete(`/admin/fields/${id}`),
  reorderFields: (fields) => studioApi.put('/admin/fields/actions/reorder', { fields }),

  getFieldGroups: (recordTypeId) => studioApi.get(`/admin/fields/groups/list?record_type_id=${recordTypeId}`),
  createFieldGroup: (data) => studioApi.post('/admin/fields/groups', data),
  deleteFieldGroup: (id) => studioApi.delete(`/admin/fields/groups/${id}`),

  getScreens: (recordTypeId) => studioApi.get(`/admin/screens?record_type_id=${recordTypeId}`),
  createScreen: (data) => studioApi.post('/admin/screens', data),
  getScreen: (id) => studioApi.get(`/admin/screens/${id}`),
  updateScreen: (id, data) => studioApi.put(`/admin/screens/${id}`, data),
  deleteScreen: (id) => studioApi.delete(`/admin/screens/${id}`),
  updateScreenLayout: (id, layout) => studioApi.put(`/admin/screens/${id}/layout`, { layout }),
  duplicateScreen: (id) => studioApi.post(`/admin/screens/${id}/duplicate`),

  getWorkflows: (recordTypeId) => studioApi.get('/admin/workflows?record_type_id=' + recordTypeId),
  getWorkflow: (id) => studioApi.get('/admin/workflows/' + id),
  createWorkflow: (data) => studioApi.post('/admin/workflows', data),
  updateWorkflow: (id, data) => studioApi.put('/admin/workflows/' + id, data),
  copyWorkflow: (id, name) => studioApi.post('/admin/workflows/' + id + '/copy', { name }),
  deleteWorkflow: (id) => studioApi.delete('/admin/workflows/' + id),
  addWorkflowStage: (wfId, data) => studioApi.post('/admin/workflows/' + wfId + '/stages', data),
  updateWorkflowStage: (wfId, sId, data) => studioApi.put('/admin/workflows/' + wfId + '/stages/' + sId, data),
  deleteWorkflowStage: (wfId, sId) => studioApi.delete('/admin/workflows/' + wfId + '/stages/' + sId),
  reorderWorkflowStages: (wfId, stageIds) => studioApi.put('/admin/workflows/' + wfId + '/stages/reorder', { stage_ids: stageIds }),
  addWorkflowTransition: (wfId, data) => studioApi.post('/admin/workflows/' + wfId + '/transitions', data),
  updateWorkflowTransition: (wfId, tId, data) => studioApi.put('/admin/workflows/' + wfId + '/transitions/' + tId, data),
  deleteWorkflowTransition: (wfId, tId) => studioApi.delete('/admin/workflows/' + wfId + '/transitions/' + tId),
  getStageScreens: (wfId, stageId) => studioApi.get('/admin/workflows/' + wfId + '/stages/' + stageId + '/screens'),
  addStageScreen: (wfId, stageId, data) => studioApi.post('/admin/workflows/' + wfId + '/stages/' + stageId + '/screens', data),
  updateStageScreen: (wfId, stageId, ssId, data) => studioApi.put('/admin/workflows/' + wfId + '/stages/' + stageId + '/screens/' + ssId, data),
  deleteStageScreen: (wfId, stageId, ssId) => studioApi.delete('/admin/workflows/' + wfId + '/stages/' + stageId + '/screens/' + ssId),
  getWorkflowStages: (wfId) => studioApi.get('/admin/workflows/' + wfId).then(res => ({ data: { stages: res.data.workflow.stages } })),
  getTaskTemplates: (wfId, stageId) => studioApi.get('/admin/workflows/' + wfId + '/stages/' + stageId + '/task-templates'),
  createTaskTemplate: (wfId, stageId, data) => studioApi.post('/admin/workflows/' + wfId + '/stages/' + stageId + '/task-templates', data),
  updateTaskTemplate: (wfId, stageId, id, data) => studioApi.put('/admin/workflows/' + wfId + '/stages/' + stageId + '/task-templates/' + id, data),
  deleteTaskTemplate: (wfId, stageId, id) => studioApi.delete('/admin/workflows/' + wfId + '/stages/' + stageId + '/task-templates/' + id),
  getWorkflowTransitions: (rtId) => studioApi.get('/admin/workflows/workflow-transitions/' + rtId),
  createWorkflowTransition: (rtId, data) => studioApi.post('/admin/workflows/workflow-transitions/' + rtId, data),
  updateWorkflowTransition2: (rtId, tId, data) => studioApi.put('/admin/workflows/workflow-transitions/' + rtId + '/' + tId, data),
  deleteWorkflowTransition2: (rtId, tId) => studioApi.delete('/admin/workflows/workflow-transitions/' + rtId + '/' + tId),
};

export const studioWorkflowTemplatesApi = {
  list: (practiceArea) => studioApi.get('/workflow-templates', { params: { practice_area: practiceArea } }).then(r => r.data),
  saveFromWorkflow: (data) => studioApi.post('/workflow-templates/save-from-workflow', data).then(r => r.data),
  setDefault: (workflowId, recordTypeId) => studioApi.post('/workflow-templates/set-default', { workflow_id: workflowId, record_type_id: recordTypeId }),
};

export default studioApi;
